import React, { createContext, useEffect } from "react";
import { setactiveItem } from "store/cards";
import {
  clearCraftBlock,
  clearCraftBlocks,
  setCraftBlocks,
} from "store/crafting";
import {
  getCurrentData,
  getIdentityFilters,
  getMatches,
  refreshMatches,
} from "services/craftIdentityService";
import {
  clearIdentityBlock,
  clearIdentityBlocks,
  getFiltersParams,
} from "utils/crafting";
import CraftBuyItems from "components/crafting/CraftBuyItems";
import CraftSelect from "components/crafting/CraftSelect";
import useCraftIdentity from "hooks/useCraftIdentity";
import CraftBlockIdentity from "components/craft-identity/CraftBlockIdentity";
import QuickCraftWindow from "components/quick-craft/QuickCraftWindow";
import MainContentLeft from "components/main-components/MainContentLeft";

const CraftSelectMemoized = React.memo(CraftSelect);
const CraftBuyItemsMemoized = React.memo(CraftBuyItems);

export const CraftIdentityContext = createContext();

const CraftIdentity = () => {
  const {
    search,
    token,
    craftBlocks,
    refreshTrigger,
    dispatch,
    firstSelectedCard,
    setFirstSelectedCard,
    activeBlock,
    setActiveBlock,
    matches,
    setMatches,
    isMatchesLoading,
    setIsMatchesLoading,
    queryParams,
    setQueryParams,
    selectCards,
    setSelectCards,
    filters,
    setFilters,
    activeFilters,
    setActiveFilters,
    selectedName,
    setSelectedName,
    resetCards,
    paginationData,
    setPaginationData,
    offsetCount,
    setOffsetCount,
    mode,
    setMode,
    marketCards,
    setMarketCards,
    nextStep,
    playSound,
    showOnboardingCards
  } = useCraftIdentity();

  const matchesLength = matches.length;

  const getDataSubUrl = (activeItem) => {
    switch (activeItem) {
      case "Category":
        return "category";
      default:
        return "date";
    }
  };

  const getCurrentOrder = () =>
    activeBlock === "Category" ? "a_z" : "low_high";

  const getMatchParams = () => {
    if (firstSelectedCard && firstSelectedCard.type !== activeBlock) {
      return `&selected=${firstSelectedCard.type.toLowerCase()}&id_selected=${firstSelectedCard.id}`;
    } else return "";
  };

  const clearBlocks = () => {
    clearIdentityBlocks(
      dispatch,
      clearCraftBlocks,
      setActiveBlock,
      setMatches,
      setQueryParams
    );
    refreshMatches(null, setMatches, setIsMatchesLoading, null);
    setFirstSelectedCard(null);
  };

  const clearBlock = (block) => {
    const foundItem = craftBlocks.find(
      (item) => item.value && item.value.id === firstSelectedCard.id
    );
    if (craftBlocks.filter((item) => item.value !== null).length === 1) {
      clearBlocks();
      setFirstSelectedCard(null);
    } else if (foundItem) {
      const newFirstSelectedCard = {
        type: foundItem.placeholder,
        id: foundItem.value.id,
      };
      setFirstSelectedCard(() => {
        clearIdentityBlock(
          dispatch,
          clearCraftBlock,
          block,
          queryParams,
          setQueryParams,
          setMatches,
          refreshMatches,
          newFirstSelectedCard,
          setIsMatchesLoading
        );
        return newFirstSelectedCard;
      });
    } else if (!foundItem) {
      clearIdentityBlock(
        dispatch,
        clearCraftBlock,
        block,
        queryParams,
        setQueryParams,
        setMatches,
        refreshMatches,
        firstSelectedCard,
        setIsMatchesLoading
      );
    }
  };

  const selectItemHandler = (card) => {
    const activeTag = activeBlock;
    const cardRarity = card.properties.find(
      (prop) => prop.title === "Rarity"
    )?.text;
    dispatch(
      setCraftBlocks({
        type: "identity",
        activeBlock,
        card,
        activeTag,
        cardRarity,
      })
    );

    dispatch(setactiveItem(null));
    nextStep();
    if (firstSelectedCard === null || activeBlock === "Category") {
      setFirstSelectedCard(() => {
        getMatches(
          card.name,
          activeBlock,
          setIsMatchesLoading,
          queryParams,
          setMatches,
          setQueryParams,
          card.id
        );
        return { type: card.type, id: card.id };
      });
    } else {
      getMatches(
        card.name,
        activeBlock,
        setIsMatchesLoading,
        queryParams,
        setMatches,
        setQueryParams,
        firstSelectedCard.id
      );
    }
  };

  const getActiveBlockData = () => {
    if (token && activeBlock !== "") {
      getCurrentData(
        resetCards,
        getDataSubUrl,
        activeBlock,
        getFiltersParams,
        activeFilters,
        offsetCount,
        getCurrentOrder,
        setSelectCards,
        setPaginationData,
        search,
        getMatchParams(),
        showOnboardingCards
      );
      dispatch(setactiveItem(null));
    } else resetCards();
  };

  const isLastCardAdded = craftBlocks.at(-1).value !== null;

  useEffect(() => {
    if (
      firstSelectedCard === null &&
      craftBlocks.some((block) => block.value !== null)
    ) {
      const firstFilledBlock = craftBlocks.find(
        (block) => block.value !== null
      );
      setFirstSelectedCard({
        type: firstFilledBlock.placeholder,
        id: firstFilledBlock.value.id,
      });
    }
    getActiveBlockData();
  }, [token, activeBlock, offsetCount, activeFilters, search]);

  useEffect(() => {
    getActiveBlockData();
  }, [firstSelectedCard]);

  useEffect(() => {
    if (queryParams.size > 0) {
      refreshMatches(
        queryParams,
        setMatches,
        setIsMatchesLoading,
        firstSelectedCard
      );
    } else {
      setMatches([]);
    }
  }, [refreshTrigger]);

  useEffect(() => {
    setOffsetCount(0);
    return () => {
      setPaginationData({});
      setActiveFilters([]);
    };
  }, [activeBlock, search]);

  useEffect(() => {
    getIdentityFilters(activeBlock, setFilters);
  }, [activeBlock]);

  return (
    <CraftIdentityContext.Provider
      value={{
        activeBlock,
        setActiveBlock,
        craftBlocks,
        clearBlock,
        clearBlocks,
        selectItemHandler,
        matches,
        setMatches,
        isMatchesLoading,
        setIsMatchesLoading,
        selectedName,
        setSelectedName,
        queryParams,
        setQueryParams,
        matchesLength,
        mode,
        setMode,
        setMarketCards,
        activeFilters,
        setActiveFilters,
        playSound,
        firstSelectedCard
      }}
    >
      <main className="main-content">
        <MainContentLeft
          getOpenedClass={() => 'opened'}
        />
        <div className="main-content__right">
          <CraftBlockIdentity />
          {mode === "select" && (
            <CraftSelectMemoized
              cards={selectCards}
              filters={filters}
              activeBlock={activeBlock}
              selectItemHandler={selectItemHandler}
              offsetCount={offsetCount}
              setOffsetCount={setOffsetCount}
              paginationData={paginationData}
              playSound={playSound}
              firstSelectedCard={firstSelectedCard}
              isLastCardAdded={isLastCardAdded}
            />
          )}
          {mode === "purchase" && (
            <CraftBuyItemsMemoized
              cards={marketCards}
              activeBlock={activeBlock}
              selectItemHandler={selectItemHandler}
              offsetCount={offsetCount}
              setOffsetCount={setOffsetCount}
              paginationData={paginationData}
              setMode={setMode}
              playSound={playSound}
            />
          )}

          { mode === 'fast-craft' && <QuickCraftWindow />}
        </div>
      </main>
    </CraftIdentityContext.Provider>
  );
};

export default CraftIdentity;
